import { AdHocReportDescriptor } from '@chiroup/core/types/AdHocReport.type';
import {
  IntegrationExchangeDispositionEnum,
  IntegrationExchangeTypeEnum,
} from '@chiroup/core/types/Integration.type';
import dayjs from 'dayjs';

export const adHocReportRegistry: AdHocReportDescriptor[] = [
  {
    id: 'patient-writeoff',
    name: 'Patient Balance Write-Offs',
    description:
      'Lists all patient balance write-offs within a date range. Useful for tax reporting and bad-debt analysis.',
    columns: [
      {
        key: 'Last Name',
        label: 'Last Name',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'First Name',
        label: 'First Name',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Patient ID',
        label: 'Patient ID',
        defaultVisible: false,
      },
      {
        key: 'Write-Off Amount',
        label: 'Write-Off Amount',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Transaction Date',
        label: 'Transaction Date',
        defaultVisible: true,
        format: 'dateTime',
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Billing Key',
        label: 'Billing Key',
        defaultVisible: false,
      },
    ],
    filters: [
      {
        key: { from: 'dateStart', to: 'dateEnd' },
        type: 'dateRange',
        label: 'Date Range',
        required: true,
        defaultValue: {
          from: dayjs().startOf('year').format('YYYY-MM-DD'),
          to: dayjs().format('YYYY-MM-DD'),
        },
      },
    ],
    executionMode: 'sync',
    allowedRoles: ['Admin', 'BusinessManager', 'Biller'],
    csvFileNamePrefix: 'patient-writeoffs',
  },
  {
    id: 'revenue-by-provider',
    name: 'Revenue by Provider',
    description:
      'Full ledger breakdown by provider: billed amounts, insurance payments, patient payments, write-offs, adjustments, and net balance.',
    columns: [
      {
        key: 'Provider Last Name',
        label: 'Provider Last Name',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Provider First Name',
        label: 'Provider First Name',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Encounter Count',
        label: 'Encounter Count',
        defaultVisible: true,
        tdClassName: 'text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Total Billed',
        label: 'Total Billed',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Insurance Payments',
        label: 'Insurance Payments',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Patient Payments',
        label: 'Patient Payments',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Write-Offs',
        label: 'Write-Offs',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Adjustments',
        label: 'Adjustments',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Net Balance',
        label: 'Net Balance',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
    ],
    filters: [
      {
        key: { from: 'dateStart', to: 'dateEnd' },
        type: 'dateRange',
        label: 'Date Range',
        required: true,
        defaultValue: {
          from: dayjs().startOf('year').format('YYYY-MM-DD'),
          to: dayjs().format('YYYY-MM-DD'),
        },
      },
    ],
    executionMode: 'sync',
    allowedRoles: ['Admin', 'BusinessManager', 'Biller'],
    csvFileNamePrefix: 'revenue-by-provider',
  },
  {
    id: 'revenue-by-cpt',
    name: 'Revenue by CPT Code',
    description:
      'Billed vs paid by CPT/service code. Joins to PayorLineItem to show actual collections per code.',
    columns: [
      {
        key: 'Code',
        label: 'Code',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Description',
        label: 'Description',
        defaultVisible: true,
      },
      {
        key: 'Line Item Count',
        label: 'Line Item Count',
        defaultVisible: true,
        tdClassName: 'text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Total Units',
        label: 'Total Units',
        defaultVisible: true,
        tdClassName: 'text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Total Billed',
        label: 'Total Billed',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Avg Per Unit',
        label: 'Avg Per Unit',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Total Paid',
        label: 'Total Paid',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Total Adjustments',
        label: 'Total Adjustments',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Outstanding',
        label: 'Outstanding',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
    ],
    filters: [
      {
        key: { from: 'dateStart', to: 'dateEnd' },
        type: 'dateRange',
        label: 'Date Range',
        required: true,
        defaultValue: {
          from: dayjs().startOf('year').format('YYYY-MM-DD'),
          to: dayjs().format('YYYY-MM-DD'),
        },
      },
    ],
    executionMode: 'sync',
    allowedRoles: ['Admin', 'BusinessManager', 'Biller'],
    csvFileNamePrefix: 'revenue-by-cpt',
  },
  {
    id: 'ar-aging',
    name: 'Accounts Receivable Aging',
    description:
      'Detail-level AR aging showing one row per unpaid invoice with aging bucket. Useful for collections prioritization.',
    columns: [
      {
        key: 'Patient Last Name',
        label: 'Patient Last Name',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Patient First Name',
        label: 'Patient First Name',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Payor Name',
        label: 'Payor Name',
        defaultVisible: true,
      },
      {
        key: 'Claim #',
        label: 'Claim #',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Billed Amount',
        label: 'Billed Amount',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Balance',
        label: 'Balance',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Invoice Status',
        label: 'Invoice Status',
        defaultVisible: true,
      },
      {
        key: 'Issued Date',
        label: 'Issued Date',
        defaultVisible: true,
        format: 'dateTime',
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Days Outstanding',
        label: 'Days Outstanding',
        defaultVisible: true,
        tdClassName: 'text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Aging Bucket',
        label: 'Aging Bucket',
        defaultVisible: true,
      },
    ],
    filters: [
      {
        key: { from: 'dateStart', to: 'dateEnd' },
        type: 'dateRange',
        label: 'Date Range',
        required: true,
        defaultValue: {
          from: dayjs().startOf('year').format('YYYY-MM-DD'),
          to: dayjs().format('YYYY-MM-DD'),
        },
      },
      {
        key: 'showOnly',
        type: 'select',
        label: 'Show Only',
        options: [
          { label: 'Payor', value: 'payor' },
          { label: 'Patient', value: 'patient' },
        ],
        placeholder: 'All',
      },
      {
        key: 'daysOutstanding',
        type: 'select',
        label: 'Days Outstanding',
        options: [
          { label: '0 - 30 Days', value: '30' },
          { label: '31 - 60 Days', value: '60' },
          { label: '61 - 90 Days', value: '90' },
          { label: '91 - 120 Days', value: '120' },
          { label: '120+ Days', value: '121' },
        ],
        placeholder: 'All',
      },
      {
        key: 'billingPriority',
        type: 'select',
        label: 'Billing Priority',
        options: [
          { label: 'Primary', value: '1' },
          { label: 'Secondary', value: '2' },
          { label: 'Tertiary', value: '3' },
        ],
        placeholder: 'All',
      },
      {
        key: 'excludeSupervisedItems',
        type: 'toggle',
        label: 'Exclude supervised items',
      },
    ],
    executionMode: 'sync',
    allowedRoles: ['Admin', 'BusinessManager', 'Biller'],
    csvFileNamePrefix: 'ar-aging',
  },
  {
    id: 'collections-rate-by-payor',
    name: 'Collections Rate by Payor',
    description:
      'Billed vs collected by payor with collection percentage and average days to payment. Useful for payor performance analysis.',
    columns: [
      {
        key: 'Payor Name',
        label: 'Payor Name',
        defaultVisible: true,
      },
      {
        key: 'Line Item Count',
        label: 'Line Item Count',
        defaultVisible: true,
        tdClassName: 'text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Total Billed',
        label: 'Total Billed',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Total Collected',
        label: 'Total Collected',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Total Adjustments',
        label: 'Total Adjustments',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Collection %',
        label: 'Collection %',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Avg Days to Payment',
        label: 'Avg Days to Payment',
        defaultVisible: true,
        tdClassName: 'text-right',
        thClassName: 'text-right',
      },
    ],
    filters: [
      {
        key: { from: 'dateStart', to: 'dateEnd' },
        type: 'dateRange',
        label: 'Date Range',
        required: true,
        defaultValue: {
          from: dayjs().startOf('year').format('YYYY-MM-DD'),
          to: dayjs().format('YYYY-MM-DD'),
        },
      },
    ],
    executionMode: 'sync',
    allowedRoles: ['Admin', 'BusinessManager', 'Biller'],
    csvFileNamePrefix: 'collections-rate-by-payor',
  },
  {
    id: 'adjustment-analysis',
    name: 'Adjustment Analysis',
    description:
      'Adjustment totals grouped by group code and payor. Useful for identifying write-off patterns and contractual adjustments.',
    columns: [
      {
        key: 'Group Code',
        label: 'Group Code',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Group Code Description',
        label: 'Group Code Description',
        defaultVisible: true,
      },
      {
        key: 'Payor Name',
        label: 'Payor Name',
        defaultVisible: true,
      },
      {
        key: 'Adjustment Count',
        label: 'Adjustment Count',
        defaultVisible: true,
        tdClassName: 'text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Total Amount',
        label: 'Total Amount',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
    ],
    filters: [
      {
        key: { from: 'dateStart', to: 'dateEnd' },
        type: 'dateRange',
        label: 'Date Range',
        required: true,
        defaultValue: {
          from: dayjs().startOf('year').format('YYYY-MM-DD'),
          to: dayjs().format('YYYY-MM-DD'),
        },
      },
    ],
    executionMode: 'sync',
    allowedRoles: ['Admin', 'BusinessManager', 'Biller'],
    csvFileNamePrefix: 'adjustment-analysis',
  },
  {
    id: 'denied-claims',
    name: 'Denied Claims',
    description:
      'Lists all denied/rejected claims within a date range. Useful for identifying denial trends and resubmission opportunities.',
    columns: [
      {
        key: 'Patient Last Name',
        label: 'Patient Last Name',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Patient First Name',
        label: 'Patient First Name',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Payor Name',
        label: 'Payor Name',
        defaultVisible: true,
      },
      {
        key: 'Claim #',
        label: 'Claim #',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Billed Amount',
        label: 'Billed Amount',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Payment Amount',
        label: 'Payment Amount',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Denial Date',
        label: 'Denial Date',
        defaultVisible: true,
        format: 'dateTime',
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Billing Key',
        label: 'Billing Key',
        defaultVisible: false,
      },
    ],
    filters: [
      {
        key: { from: 'dateStart', to: 'dateEnd' },
        type: 'dateRange',
        label: 'Date Range',
        required: true,
        defaultValue: {
          from: dayjs().startOf('year').format('YYYY-MM-DD'),
          to: dayjs().format('YYYY-MM-DD'),
        },
      },
    ],
    executionMode: 'sync',
    allowedRoles: ['Admin', 'BusinessManager', 'Biller'],
    csvFileNamePrefix: 'denied-claims',
  },
  {
    id: 'claim-turnaround-time',
    name: 'Claim Turnaround Time',
    description:
      'Average, min, and max days from claim submission to remittance by payor. Useful for monitoring payor responsiveness.',
    columns: [
      {
        key: 'Payor Name',
        label: 'Payor Name',
        defaultVisible: true,
      },
      {
        key: 'Claim Count',
        label: 'Claim Count',
        defaultVisible: true,
        tdClassName: 'text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Avg Days',
        label: 'Avg Days',
        defaultVisible: true,
        tdClassName: 'text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Min Days',
        label: 'Min Days',
        defaultVisible: true,
        tdClassName: 'text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Max Days',
        label: 'Max Days',
        defaultVisible: true,
        tdClassName: 'text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Total Billed',
        label: 'Total Billed',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Total Paid',
        label: 'Total Paid',
        defaultVisible: true,
        format: 'currency',
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
    ],
    filters: [
      {
        key: { from: 'dateStart', to: 'dateEnd' },
        type: 'dateRange',
        label: 'Date Range',
        required: true,
        defaultValue: {
          from: dayjs().startOf('year').format('YYYY-MM-DD'),
          to: dayjs().format('YYYY-MM-DD'),
        },
      },
    ],
    executionMode: 'sync',
    allowedRoles: ['Admin', 'BusinessManager', 'Biller'],
    csvFileNamePrefix: 'claim-turnaround-time',
  },
  {
    id: 'edi-traffic',
    name: 'EDI Traffic',
    description:
      'Shows all EDI (clearinghouse) traffic within a date range. Useful for diagnosing clearinghouse connectivity issues such as missing 835/remit files.',
    columns: [
      {
        key: 'Date/Time',
        label: 'Date/Time',
        defaultVisible: true,
        format: 'dateTime',
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Type',
        label: 'Type',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Direction',
        label: 'Direction',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Disposition',
        label: 'Disposition',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'File Name',
        label: 'File Name',
        defaultVisible: true,
      },
      {
        key: 'Bytes',
        label: 'Bytes',
        defaultVisible: false,
        tdClassName: 'whitespace-nowrap text-right',
        thClassName: 'text-right',
      },
      {
        key: 'Comment',
        label: 'Comment',
        defaultVisible: false,
      },
      {
        key: 'Exchange ID',
        label: 'Exchange ID',
        defaultVisible: false,
      },
    ],
    filters: [
      {
        key: { from: 'dateStart', to: 'dateEnd' },
        type: 'dateRange',
        label: 'Date Range',
        required: true,
        defaultValue: {
          from: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
          to: dayjs().format('YYYY-MM-DD'),
        },
      },
      {
        key: 'type',
        type: 'select',
        label: 'Type',
        options: [
          { label: 'Claim (837)', value: IntegrationExchangeTypeEnum.Claim },
          { label: 'Remit (835)', value: IntegrationExchangeTypeEnum.Remit },
          {
            label: 'Claim Acknowledgment (277)',
            value: IntegrationExchangeTypeEnum.ClaimAcknowledgment,
          },
          {
            label: 'Implementation Acknowledgment (999)',
            value: IntegrationExchangeTypeEnum.ImplementationAcknowledgment,
          },
          {
            label: 'Eligibility Request (270)',
            value: IntegrationExchangeTypeEnum.InsuranceEligibilityRequest,
          },
          {
            label: 'Eligibility Response (271)',
            value: IntegrationExchangeTypeEnum.InsuranceEligibilityResponse,
          },
        ],
        placeholder: 'All',
      },
      {
        key: 'disposition',
        type: 'select',
        label: 'Disposition',
        options: [
          {
            label: 'Pending',
            value: IntegrationExchangeDispositionEnum.Pending,
          },
          {
            label: 'Transferred',
            value: IntegrationExchangeDispositionEnum.Transferred,
          },
          {
            label: 'Rejected',
            value: IntegrationExchangeDispositionEnum.Rejected,
          },
          {
            label: 'Accepted',
            value: IntegrationExchangeDispositionEnum.Accepted,
          },
          {
            label: 'Processed',
            value: IntegrationExchangeDispositionEnum.Processed,
          },
          {
            label: 'Error',
            value: IntegrationExchangeDispositionEnum.Error,
          },
          {
            label: 'Checksum Failure',
            value: IntegrationExchangeDispositionEnum.ChecksumFailure,
          },
          {
            label: 'Reversed',
            value: IntegrationExchangeDispositionEnum.Reversed,
          },
        ],
        placeholder: 'All',
      },
    ],
    executionMode: 'sync',
    allowedRoles: ['Admin', 'BusinessManager', 'Biller'],
    csvFileNamePrefix: 'clearinghouse-traffic',
  },
  {
    id: 'inactive-insurance-claims',
    name: 'Inactive Insurance Claims',
    description:
      'Identifies submitted invoices where the insurance policy used is inactive or deleted. Useful for catching claims filed against stale insurance records.',
    columns: [
      {
        key: 'Billing Key',
        label: 'Billing Key',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Last Name',
        label: 'Last Name',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'First Name',
        label: 'First Name',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Date of Service',
        label: 'Date of Service',
        defaultVisible: true,
        format: 'dateTime',
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Insurance',
        label: 'Insurance',
        defaultVisible: true,
      },
      {
        key: 'Invoice Status',
        label: 'Invoice Status',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Insurance Status',
        label: 'Insurance Status',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Payor ID',
        label: 'Payor ID',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Member ID',
        label: 'Member ID',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
      {
        key: 'Group ID',
        label: 'Group ID',
        defaultVisible: true,
        tdClassName: 'whitespace-nowrap',
      },
    ],
    filters: [],
    executionMode: 'sync',
    allowedRoles: ['Admin', 'BusinessManager', 'Biller'],
    csvFileNamePrefix: 'inactive-insurance-claims',
  },
];
